import { isSameDay, format, getDate } from 'date-fns'
import { bool, func, instanceOf, number, object, oneOf } from 'prop-types'
import { useCallback } from 'react'
import { renderBreakdownTooltipContent } from '@opallabs/calendar'
import { Timescale } from '@opallabs/concepts'
import { kuiperClassNames } from '@opallabs/kuiper'
import { Tooltip } from '@opallabs/lapidary'
import { useMomentDragContext } from '../../WeekView/MomentDragContext'
import { formatDayHeading, HistogramBar } from '../HistogramBar'
import { useHistogramDayDropHandlers } from '../useHistogramDayDropHandlers'
import DropWarner from '../Week/DropWarner'
import './styles.scss'

const Day = ({
  breakdown,
  date,
  focusedDay,
  histogramData,
  isContentMode,
  largestDayCount,
  mode,
  onClick,
  onRescheduleContent,
  onUpdateMoment,
  story,
  style,
}) => {
  const { draggedMoment } = useMomentDragContext()
  const {
    isActiveDropTarget,
    isForbiddenDropTarget,
    isForbiddenMomentDrop,
    isNotWithinStory,
    isSameDateAsDragged,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
  } = useHistogramDayDropHandlers({
    date,
    story,
    draggedMoment,
    onRescheduleContent,
    onUpdateMoment,
  })

  const TODAY = new Date()
  const daySize = histogramData[format(date, 'yyyy-MM-dd')] || 0
  // 52px: the band, less the strip the month label and the day
  // number take above it.
  const dayHeight = 52 * (daySize / largestDayCount)
  const isFocused = isSameDay(focusedDay, date)

  const onClickDay = useCallback(() => {
    if (mode !== Timescale.Day) return
    onClick(date)
  }, [date, mode, onClick])

  const isToday = isSameDay(TODAY, date)
  const dateNumber = getDate(date)

  const dayKey = format(date, 'yyyy-MM-dd')
  // `breakdownByDate` is only populated for the active 7 days, so the
  // lookup self-gates without a separate "is in active week" check.
  const dayLeafBreakdown = breakdown?.breakdownByDate?.[dayKey]
  const dayBarBreakdown = breakdown?.barBreakdownByDate?.[dayKey]
  const isInActiveWeek = dayLeafBreakdown != null
  // The breakdown is tallied from the Week View content slice, which the Day
  // calendar never fetches — so the server histogram (`daySize`) is the count
  // of record here. (Week View, where the slice is loaded, sums the breakdown
  // instead so the count reconciles with the tooltip rows.)
  const tooltipContent = renderBreakdownTooltipContent({
    breakdown: dayLeafBreakdown,
    colorFor: breakdown?.colorFor,
    heading: formatDayHeading(date),
    isContentMode: Boolean(isContentMode),
    nameFor: breakdown?.nameFor,
    parentOf: breakdown?.parentOf,
    totalCount: daySize,
  })

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <div
      onClick={onClickDay}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={style}
      className={kuiperClassNames('histogramDay', `mode-${mode}`, {
        isActiveDropTarget,
        isFocused,
        isForbiddenDropTarget,
        isNotWithinStory,
        isToday,
      })}
    >
      {isToday && (
        <div className={kuiperClassNames('todayBar')}>
          <div className={kuiperClassNames('todayBarInner')} />
        </div>
      )}

      <div className={kuiperClassNames('histogramDay-metaData')}>
        {dateNumber === 1 && (
          <span className={kuiperClassNames('monthLabel')}>
            <strong>{format(date, 'MMMM')}</strong> {format(date, 'yyyy')}
          </span>
        )}
        <span className={kuiperClassNames('date')}>{dateNumber}</span>
        <Tooltip
          content={tooltipContent}
          delay={100}
          side="bottom"
          sideOffset={12}
          zIndex={999}
        >
          <HistogramBar
            breakdown={dayBarBreakdown}
            colorFor={breakdown?.colorFor}
            height={dayHeight}
            shouldStack={
              isInActiveWeek && Boolean(breakdown?.shouldStackSegments)
            }
            totalCount={daySize}
          />
        </Tooltip>
      </div>

      {isForbiddenMomentDrop && (
        <DropWarner
          date={date}
          isNotWithinStory={isNotWithinStory}
          isSameDateAsDragged={isSameDateAsDragged}
          story={story}
        />
      )}
    </div>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
}

Day.propTypes = {
  breakdown: object,
  date: instanceOf(Date).isRequired,
  focusedDay: instanceOf(Date).isRequired,
  histogramData: object,
  isContentMode: bool,
  largestDayCount: number,
  mode: oneOf([Timescale.Day, Timescale.Month, Timescale.Week]),
  onClick: func.isRequired,
  onRescheduleContent: func,
  onUpdateMoment: func.isRequired,
  story: object,
  style: object,
}

export default Day
